import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import BentoCard from '../ui/BentoCard'
import AdherenceRing from '../ui/AdherenceRing'
import { CollapsedDisclaimer } from '../compliance/CollapsedDisclaimer'
import { sanitizeCoachText } from '../../lib/aiResponseValidator'
import { buildProteinAlertMessage } from '../../lib/nutritionAlertMessage'
import { KILO } from '../../constants/KILO_THEME'
import type { DailyNutritionSummary } from '../../lib/mealLogCore'

type NutritionPeriod = 'Today' | 'Week'

type NutritionAdherenceCardProps = {
  todaySummary: DailyNutritionSummary | null
  yesterdaySummary: DailyNutritionSummary | null
  weekTargetKcal: number
  weekActualKcal: number
  coachNote?: string | null
  isFirstDay?: boolean
  embedded?: boolean
  hideHeading?: boolean
  hideDisclaimer?: boolean
  /** Briefings audit shows yesterday only — no today/week toggle */
  primaryFocus?: 'today' | 'yesterday'
}

function totalMealSlots(summary: DailyNutritionSummary): number {
  return (
    summary.slots_on_plan +
    summary.slots_substituted +
    summary.slots_skipped +
    summary.slots_unlogged
  )
}

function yesterdayMealGapLines(
  summary: DailyNutritionSummary,
  t: (key: string, options?: Record<string, unknown>) => string,
): string[] {
  const pluralKey = (base: string, count: number) =>
    `${base}_${count === 1 ? 'one' : 'other'}`

  const lines: string[] = []

  if (summary.slots_logged === 0 && summary.slots_unlogged > 0) {
    lines.push(
      t(`coach.nutritionAdherence.${pluralKey('yesterdayAllUnlogged', summary.slots_unlogged)}`, {
        count: summary.slots_unlogged,
      }),
    )
    return lines
  }

  if (summary.slots_unlogged > 0) {
    lines.push(
      t(`coach.nutritionAdherence.${pluralKey('yesterdayUnlogged', summary.slots_unlogged)}`, {
        count: summary.slots_unlogged,
      }),
    )
  }
  if (summary.slots_substituted > 0) {
    lines.push(
      t(`coach.nutritionAdherence.${pluralKey('yesterdayOffPlan', summary.slots_substituted)}`, {
        count: summary.slots_substituted,
      }),
    )
  }
  if (summary.slots_skipped > 0) {
    lines.push(
      t(`coach.nutritionAdherence.${pluralKey('yesterdaySkipped', summary.slots_skipped)}`, {
        count: summary.slots_skipped,
      }),
    )
  }

  return lines
}

function MacroDistributionBar({
  proteinG,
  carbsG,
  fatG,
  title,
}: {
  proteinG: number
  carbsG: number
  fatG: number
  title: string
}) {
  const segments = useMemo(() => {
    const proteinKcal = proteinG * 4
    const carbsKcal = carbsG * 4
    const fatKcal = fatG * 9
    const totalKcal = proteinKcal + carbsKcal + fatKcal
    if (totalKcal <= 0) return null
    return [
      { value: proteinKcal, color: '#4caf50' },
      { value: carbsKcal, color: '#3b82f6' },
      { value: fatKcal, color: '#f59e0b' },
    ].map(({ value, color }) => ({
      color,
      pct: (value / totalKcal) * 100,
    }))
  }, [proteinG, carbsG, fatG])

  if (!segments) return null

  return (
    <div className="mb-2 mt-3">
      <p className="mb-1.5 text-[10px] uppercase tracking-wider text-neutral-500 dark:text-neutral-500">
        {title}
      </p>
      <div className="flex h-1.5 overflow-hidden rounded-full gap-0.5">
        {segments.map(({ pct, color }, i) => (
          <div
            key={i}
            style={{ width: `${pct}%`, background: color }}
            className="transition-all duration-500"
          />
        ))}
        <div className="flex-1 rounded-r-full bg-neutral-200 dark:bg-neutral-800" />
      </div>
      <div className="mt-1.5 flex gap-3">
        {[
          { label: 'P', value: proteinG, color: '#4caf50' },
          { label: 'C', value: carbsG, color: '#3b82f6' },
          { label: 'F', value: fatG, color: '#f59e0b' },
        ].map(({ label, value, color }) => (
          <span key={label} className="text-[10px] text-neutral-500 dark:text-neutral-500">
            <span style={{ color }}>{label}</span> {Math.round(value)}g
          </span>
        ))}
      </div>
    </div>
  )
}

const NutritionAdherenceCard = ({
  todaySummary,
  yesterdaySummary,
  weekTargetKcal,
  weekActualKcal,
  coachNote,
  isFirstDay = false,
  embedded = false,
  hideHeading = false,
  hideDisclaimer = false,
  primaryFocus = 'today',
}: NutritionAdherenceCardProps) => {
  const { t } = useTranslation()
  const [activePeriod, setActivePeriod] = useState<NutritionPeriod>('Today')
  const showPeriodToggle = primaryFocus === 'today'

  const yesterdayMealGaps =
    yesterdaySummary != null &&
    (yesterdaySummary.slots_substituted > 0 ||
      yesterdaySummary.slots_skipped > 0 ||
      yesterdaySummary.slots_unlogged > 0)

  const yesterdayGapLines = yesterdaySummary
    ? yesterdayMealGapLines(yesterdaySummary, t)
    : []

  const emptyShell = embedded ? (
    <div className={KILO.embeddedSection}>
      <p className={KILO.sectionLabel}>{t('coach.nutritionAdherence.title')}</p>
      <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
        {isFirstDay ? t('coach.nutritionAdherence.emptyStateFirstDay') : t('coach.nutritionAdherence.emptyState')}
      </p>
    </div>
  ) : (
    <BentoCard gradient="slate" className="col-span-2">
      <p className={KILO.sectionLabel}>{t('coach.nutritionAdherence.title')}</p>
      <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
        {isFirstDay ? t('coach.nutritionAdherence.emptyStateFirstDay') : t('coach.nutritionAdherence.emptyState')}
      </p>
    </BentoCard>
  )

  if (!todaySummary && !yesterdaySummary) {
    return emptyShell
  }

  const focusSummary =
    primaryFocus === 'yesterday' ? yesterdaySummary : todaySummary ?? yesterdaySummary
  const displaySummary = focusSummary ?? yesterdaySummary!
  const adherence = displaySummary.adherence_pct ?? 0

  const dailyKcalActual = displaySummary.actual.calories_kcal ?? 0
  const dailyKcalTarget = displaySummary.planned.calories_kcal ?? 0

  const kcalDisplay =
    showPeriodToggle && activePeriod === 'Week' ? weekActualKcal : dailyKcalActual

  const kcalTarget =
    showPeriodToggle && activePeriod === 'Week' ? weekTargetKcal : dailyKcalTarget

  const periodSubtitle =
    showPeriodToggle && activePeriod === 'Week'
      ? t('coach.nutritionAdherence.weekTargetKcal', { kcal: kcalTarget.toLocaleString() })
      : primaryFocus === 'yesterday'
        ? t('coach.nutritionAdherence.yesterdayTargetKcal', { kcal: kcalTarget.toLocaleString() })
        : t('coach.nutritionAdherence.todayTargetKcal', { kcal: kcalTarget.toLocaleString() })

  const macroSource =
    showPeriodToggle && activePeriod === 'Week' ? null : displaySummary.actual
  const proteinG = macroSource?.protein_g
  const carbsG = macroSource?.carbs_g
  const fatG = macroSource?.fat_g
  const showMacroBar =
    proteinG != null &&
    carbsG != null &&
    fatG != null &&
    (proteinG > 0 || carbsG > 0 || fatG > 0)

  const proteinShortfall =
    primaryFocus === 'today' && todaySummary != null
      ? todaySummary.planned.protein_g - todaySummary.actual.protein_g
      : null
  const proteinAlert = buildProteinAlertMessage(proteinShortfall, new Date().getHours())

  const mealSummary = primaryFocus === 'today' ? (todaySummary ?? displaySummary) : displaySummary
  const mealTotal = totalMealSlots(mealSummary)

  const content = (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          {!hideHeading ? (
            <p className={KILO.sectionLabel}>
              {primaryFocus === 'yesterday'
                ? t('coach.nutritionAdherence.yesterdayVsPlan')
                : t('coach.nutritionAdherence.todayVsPlan')}
            </p>
          ) : null}
          <p className={`${hideHeading ? '' : 'mt-1 '}text-2xl font-black ${KILO.text.primary}`}>
            {kcalDisplay.toLocaleString()} kcal
          </p>
          <div className="mt-1">
            <p className="text-[11px] text-neutral-500 dark:text-neutral-500">{periodSubtitle}</p>
            {showPeriodToggle ? (
              <div className="mt-2 flex gap-1">
                {(['Today', 'Week'] as const).map((period) => (
                  <button
                    key={period}
                    type="button"
                    onClick={() => setActivePeriod(period)}
                    className={`rounded-full px-3 py-1 text-[10px] transition-colors ${
                      activePeriod === period
                        ? 'bg-neutral-200 text-neutral-900 dark:bg-neutral-800 dark:text-neutral-100'
                        : 'text-neutral-500 dark:text-neutral-500'
                    }`}
                  >
                    {period === 'Today'
                      ? t('coach.nutritionAdherence.periodToday')
                      : t('coach.nutritionAdherence.periodWeek')}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </div>
        {mealTotal > 0 ? (
          <AdherenceRing
            percentage={adherence}
            label={t('coach.nutritionAdherence.adherence')}
          />
        ) : null}
      </div>

      {showMacroBar ? (
        <MacroDistributionBar
          proteinG={proteinG!}
          carbsG={carbsG!}
          fatG={fatG!}
          title={t('coach.nutritionAdherence.macrosToday')}
        />
      ) : null}

      {mealTotal > 0 ? (
        <div className="mt-4 flex flex-wrap gap-2 text-xs">
          <span className="rounded-lg bg-kilo-accent/10 px-2 py-1 text-kilo-accent">
            {t('coach.nutritionAdherence.onPlanWithTotal', {
              count: mealSummary.slots_on_plan,
              total: mealTotal,
            })}
          </span>
          {mealSummary.slots_substituted > 0 ? (
            <span className="rounded-lg border border-kilo-line bg-kilo-nested px-2 py-1 text-kilo-text">
              {t('coach.nutritionAdherence.substituted', { count: mealSummary.slots_substituted })}
            </span>
          ) : null}
          {mealSummary.slots_skipped > 0 ? (
            <span className="rounded-lg bg-kilo-nested px-2 py-1 text-neutral-500 dark:text-neutral-400">
              {t('coach.nutritionAdherence.skipped', { count: mealSummary.slots_skipped })}
            </span>
          ) : null}
        </div>
      ) : null}

      {proteinAlert ? (
        <div className="mb-3 mt-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 dark:border-amber-900/40 dark:bg-amber-950/20">
          <div className="mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border border-amber-400 dark:border-amber-700">
            <span className="text-[8px] text-amber-600 dark:text-amber-500">!</span>
          </div>
          <p className="text-xs leading-relaxed text-amber-700 dark:text-amber-400">{proteinAlert}</p>
        </div>
      ) : null}

      {primaryFocus === 'today' && yesterdayMealGaps && yesterdaySummary ? (
        <div className="mt-3 rounded-xl bg-neutral-100 px-3 py-2.5 dark:bg-neutral-900">
          <p className="mb-1 text-[9px] uppercase tracking-wider text-neutral-500 dark:text-neutral-500">
            {t('coach.nutritionAdherence.yesterdayMealLogTitle')}
          </p>
          <p className="text-xs leading-relaxed text-neutral-500 dark:text-neutral-500">
            {yesterdayGapLines.join(' · ')}
          </p>
        </div>
      ) : null}

      {coachNote && !hideDisclaimer && !yesterdayMealGaps ? (
        <>
          <p className="mt-3 text-sm text-neutral-500 dark:text-neutral-400">
            {sanitizeCoachText(coachNote, 'default')}
          </p>
          <CollapsedDisclaimer />
        </>
      ) : null}
    </>
  )

  if (embedded) {
    return <div className={KILO.embeddedSection}>{content}</div>
  }

  return (
    <BentoCard gradient="slate" className="col-span-2">
      {content}
    </BentoCard>
  )
}

export default NutritionAdherenceCard
